import { createContext, useContext } from 'react';
import { MainMenu, PlannerConfig, SubMenuOption, Widget } from 'rangeConfig/@types';
import isWidget from 'rangeConfig/shared/utils/isWidget';
import { MenuType } from 'analytics/interaction';
import { usePlannerConfig } from './plannerConfig';

/**
 * The widget context is used when exposing planner config through context.
 * All widget components are automatically wrapped in a provider with data
 * related to the widget definition. These are later passed to the behavior
 * wrappers and used to report events
 */

export type WidgetMeta = {
  menuType: MenuType;
  groupId: string;
  subMenuId?: string;
  widgetId?: string;
  expanded?: boolean;
};

export type WidgetContextValue = WidgetMeta & {
  config?: MainMenu | SubMenuOption;
  configId: string;
};

export const WidgetContext = createContext<WidgetContextValue>({
  groupId: '',
  menuType: 'main',
  widgetId: '',
  configId: ''
});

// Use this inside a widget to get context data
export const useWidget = () => useContext(WidgetContext);

export const withWidgetContext = <P extends object>(
  Component: React.FunctionComponent<P>,
  value: Omit<WidgetContextValue, 'configId'>
): React.FunctionComponent<P> => (p: P) => {
  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const productGroup = productGroups.find(({ id }) => id === value.groupId);
  const menuConfig = productGroup?.mainMenu;
  const subMenuConfig = menuConfig?.subMenu?.options.find(({ id }) => id === value.subMenuId);
  const configId = value.subMenuId ?? value.groupId;
  const config = subMenuConfig ?? menuConfig;

  return (
    <WidgetContext.Provider value={{ ...value, configId, config }}>
      <Component {...p} />
    </WidgetContext.Provider>
  );
};

const transformWidget = (
  widget: Widget,
  groupId: string,
  menuType: MenuType,
  subMenuId?: string
) => ({
  ...widget,
  component: withWidgetContext(widget.component, {
    widgetId: widget.id,
    groupId,
    menuType,
    subMenuId
  })
});

export const applyWidgetContext = (plannerConfig: PlannerConfig) =>
  plannerConfig.productGroups.forEach(productGroup => {
    productGroup.mainMenu.subMenu?.options.forEach(subMenuOption => {
      subMenuOption.widgets = subMenuOption.widgets.map(widget =>
        transformWidget(widget, productGroup.id, 'main', subMenuOption.id)
      );
    });

    productGroup.mainMenu.widgets = productGroup.mainMenu.widgets.map(widget =>
      transformWidget(widget, productGroup.id, 'main')
    );

    productGroup.selectedMenu.content = productGroup.selectedMenu.content.map(c =>
      isWidget(c) ? transformWidget(c as Widget, productGroup.id, 'selected') : c
    );
  });
